package com.nonopn.skydome.common.app.pages.probes

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.promocodes.PromoCodeField
import com.nonopn.skydome.common.app.pages.promocodes.isPromoFieldVisible
import com.nonopn.skydome.common.app.api.impl.AccountAction
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouterProbes
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.probe_create_count
import com.nonopn.skywatcher.common.app.res.probe_create_empty
import com.nonopn.skywatcher.common.app.res.probe_create_hint
import com.nonopn.skywatcher.common.app.res.probe_create_hint_trial
import com.nonopn.skywatcher.common.app.res.probe_create_item
import com.nonopn.skywatcher.common.app.res.probe_create_legend_existing
import com.nonopn.skywatcher.common.app.res.probe_create_legend_new
import com.nonopn.skywatcher.common.app.res.probe_create_pay
import com.nonopn.skywatcher.common.app.res.probe_create_title
import com.nonopn.skywatcher.common.app.res.probe_create_title_trial
import com.nonopn.skywatcher.common.app.res.probe_create_remove
import com.nonopn.skywatcher.common.app.res.probe_create_start_trial
import com.nonopn.skywatcher.common.app.res.probe_create_submitting
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.theme.AppColor
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.viewmodel.rememberViewModel
import org.jetbrains.compose.resources.stringResource
import com.nonopn.skydome.common.app.pages.fullscreenmap.FullscreenMapContainer
import ovh.plrapps.mapcompose.ui.MapUI

private val ExistingRed = Color(0xFFE63B2E)

/** Screen 3 of the mock-up: place, drag and remove new probes on the map, then pay (or start the trial). */
@Composable
fun ProbeCreationScreen(modifier: Modifier = Modifier) {
    val appModel: AppModel = LocalApp.current
    val visible = rememberProbeCreationVisible(appModel)
    // Trial mode is requested by the caller, once
    val trialMode = remember { appModel.pendingProbeTrial.also { appModel.pendingProbeTrial = false } }

    LaunchedEffect(trialMode) {
        appModel.setAppBarState(
            AppBarState.Localized(
                title = if (trialMode) Res.string.probe_create_title_trial else Res.string.probe_create_title,
                actions = emptyList()
            )
        )
    }

    if (!visible) {
        // Feature flag off: this screen is not reachable
        LaunchedEffect(Unit) { appModel.show(RouterProbes.navigateTo()) }
        return
    }
    ProbeCreationContent(appModel, trialMode, modifier)
}

@Composable
private fun ProbeCreationContent(appModel: AppModel, trialMode: Boolean, modifier: Modifier) {
    val model = rememberViewModel { ProbeCreationModel(appModel, trialMode) }
    val state by model.states.collectAsState()
    val uriHandler = LocalUriHandler.current

    state.error?.let { error ->
        ProbeFailureDialog(
            error = error,
            onDismiss = { model.dismissError() },
            onPayExisting = { model.payExistingTrial(uriHandler) },
            canPayExisting = state.actions.allows(AccountAction.Upgrade)
        )
    }

    Column(modifier = modifier.fillMaxSize().background(AppColor.StrikeBackground)) {
        FullscreenMapContainer(
            modifier = Modifier.weight(1f).fillMaxWidth(),
            // The top right corner is used by the close (✕) button of this screen
            controlsAlignment = Alignment.CenterEnd,
            controlsPadding = 10.dp,
        ) { isFullscreen ->
            MapUI(modifier = Modifier.fillMaxSize(), state = model.mapState)
            Legend(
                state.trialMode,
                Modifier.align(Alignment.TopStart).padding(start = 10.dp, top = 10.dp, end = 56.dp)
            )
            if (!isFullscreen) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(10.dp)
                        .size(36.dp)
                        .clip(CircleShape)
                        .background(Color.White.copy(alpha = 0.92f))
                        .clickable { appModel.show(RouterProbes.navigateTo()) },
                    contentAlignment = Alignment.Center
                ) {
                    TextNormal("✕", fontSize = 16.sp, color = Color(0xFF1A2B4A))
                }
            }
        }
        BottomPanel(state, model, onSubmit = { model.submit(uriHandler) })
    }
}

@Composable
private fun Legend(trialMode: Boolean, modifier: Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .background(Color.White.copy(alpha = 0.92f))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        TextNormal(
            stringResource(if (trialMode) Res.string.probe_create_hint_trial else Res.string.probe_create_hint),
            fontSize = 12.sp,
            color = Color(0xFF1A2B4A)
        )
        Row(verticalAlignment = Alignment.CenterVertically) {
            ProbeDot(ExistingRed, 10)
            Spacer(Modifier.width(4.dp))
            TextNormal(
                stringResource(Res.string.probe_create_legend_existing),
                fontSize = 11.sp,
                color = Color(0xFF1A2B4A)
            )
            Spacer(Modifier.width(12.dp))
            ProbeDot(ProbeOrange, 10)
            Spacer(Modifier.width(4.dp))
            TextNormal(
                stringResource(Res.string.probe_create_legend_new),
                fontSize = 11.sp,
                color = Color(0xFF1A2B4A)
            )
        }
    }
}

@Composable
private fun BottomPanel(state: ProbeCreationState, model: ProbeCreationModel, onSubmit: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppColor.StrikeCard)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        TextNormal(
            stringResource(Res.string.probe_create_count, state.drafts.size),
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
            color = AppColor.StrikeText
        )
        Column(
            modifier = Modifier.heightIn(max = 160.dp).verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            ProbeDrafts.renumber(state.drafts).forEachIndexed { index, draft ->
                val id = state.drafts[index].id
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier.size(22.dp).background(ProbeOrange, CircleShape),
                        contentAlignment = Alignment.Center
                    ) {
                        TextNormal(
                            "${draft.number}",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                    Spacer(Modifier.width(8.dp))
                    TextNormal(
                        stringResource(
                            Res.string.probe_create_item,
                            draft.number,
                            formatLatLon(draft.latitude, draft.longitude)
                        ),
                        modifier = Modifier.weight(1f),
                        fontSize = 13.sp,
                        color = AppColor.StrikeText
                    )
                    TextNormal(
                        stringResource(Res.string.probe_create_remove),
                        modifier = Modifier.clickable { model.removeDraft(id) }.padding(6.dp),
                        fontSize = 13.sp,
                        color = ExistingRed
                    )
                }
            }
        }
        // "Promo code" before the payment: logged in account, paid creation, no running subscription
        val appState by LocalApp.current.states.collectAsState()
        val loggedIn = appState.apiProvider?.let { it.apiNormal != null || it.apiAdmin != null } == true
        if (isPromoFieldVisible(loggedIn, state.subscriptionRunning, isDemoRequest = state.trialMode)) {
            PromoCodeField(
                state = state.promo,
                onInput = { model.onPromoInput(it) },
                onApply = { model.applyPromo() },
                onRemove = { model.removePromo() },
                enabled = !state.submitting,
            )
        }
        val canSubmit = state.drafts.isNotEmpty() && !state.submitting && !state.promo.checking
        val label = when {
            state.submitting -> stringResource(Res.string.probe_create_submitting)
            state.drafts.isEmpty() -> stringResource(Res.string.probe_create_empty)
            state.trialMode -> stringResource(Res.string.probe_create_start_trial)
            else -> stringResource(Res.string.probe_create_pay)
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(10.dp))
                .background(if (canSubmit) AppColor.StrikePrimary else AppColor.StrikePrimary.copy(alpha = 0.35f))
                .clickable(enabled = canSubmit) { onSubmit() }
                .padding(vertical = 14.dp),
            contentAlignment = Alignment.Center
        ) {
            TextNormal(
                label,
                fontSize = 14.sp,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                textAlign = TextAlign.Center
            )
        }
    }
}
